import { IAudioPopup } from "../../models/interactions/AudioPopup";
import { IConnectQuiz } from "../../models/interactions/ConnectQuiz";
import Interaction, { InteractionType } from "../../models/interactions/Interaction";
import { IImageDragAndDropQuiz } from "../../models/interactions/ImageDragAndDropQuiz";
import { ILabelDragAndDropQuiz } from "../../models/interactions/LabelDragAndDropQuiz";
import { IPhotoRangeQuiz } from "../../models/interactions/PhotoRangeQuiz";
import { IPowerSlide } from "../../models/interactions/PowerSlideHelper";
import { ISlideshow } from "../../models/interactions/Slideshow";
import { IAnswer, IFormQuiz } from "../../models/Quiz";

/** One image an interaction shows, which can get its own version per language. */
export interface LocalizableImage {
  /** The answer's id (stable across saves, unique across the training), or the interaction's own. */
  id: string;
  /** What the strip shows under the image: the answer's letter. Unused for a single image. */
  label: string;
  assetId: string | undefined;
  /** The image the rest sits on (a drag-and-drop background, a photo range's base): an icon, not a letter. */
  background?: boolean;
  /** Images that belong together in the strip under one caption (a PowerSlide slide's number). */
  group?: number;
  /** For items that are named, not lettered (the audio popup's image and audio): an i18n key. */
  nameKey?: string;
}

const letter = (index: number) => String.fromCharCode(65 + index);
const fromAnswers = (answers: IAnswer[] | undefined): LocalizableImage[] =>
  (answers ?? []).map((answer, index) => ({ id: answer.id, label: letter(index), assetId: answer.assetId || undefined }));

/** An interaction with one image: no strip, so the id and label only need to be stable. */
const single = (interaction: Interaction, assetId: string | undefined): LocalizableImage[] => [
  { id: interaction.id, label: "", assetId: assetId || undefined },
];

const background = (id: string, assetId: string | undefined): LocalizableImage => ({
  id,
  label: "",
  assetId: assetId || undefined,
  background: true,
});

const usesImage = (interaction: Interaction) =>
  interaction.properties?.some((p) => p.name === "useImage" && p.value === true) ?? false;

/**
 * The images of an interaction that can be localized, in the order its preview shows them. Empty for
 * interactions without (supported) images. See the inventory in docs/LocalizeImagesPlan.md.
 */
export const localizableImages = (interaction: Interaction | undefined): LocalizableImage[] => {
  switch (interaction?.type) {
    case InteractionType.MultipleChoiceGraphicsQuiz:
    case InteractionType.MultiSelectGraphicsQuiz:
    case InteractionType.OrderGraphicsQuiz:
      return fromAnswers((interaction as IFormQuiz).answers);
    case InteractionType.ConnectGraphicsQuiz:
      // Only side B of a connect quiz carries images.
      return fromAnswers((interaction as IConnectQuiz).answersB);
    case InteractionType.ImagePopup:
    case InteractionType.VideoPopup:
      return single(interaction, (interaction as Interaction & { assetId?: string }).assetId);
    case InteractionType.MultipleChoiceQuiz:
    case InteractionType.MultiselectQuiz:
      // The text quizzes only show their image with the "useImage" property on.
      return usesImage(interaction) ? single(interaction, (interaction as IFormQuiz).assetId) : [];
    case InteractionType.ImageButtonQuiz:
      return fromAnswers((interaction as IFormQuiz).answers);
    case InteractionType.Slideshow:
      // Slides are bare asset ids: their position is their identity, numbered as the preview counts them.
      return ((interaction as ISlideshow).assetIds ?? []).map((assetId, index) => ({
        id: `${interaction.id}:slide:${index}`,
        label: String(index + 1),
        assetId: assetId || undefined,
      }));
    case InteractionType.ImageDragAndDropQuiz: {
      const quiz = interaction as IImageDragAndDropQuiz;
      return [
        background(`${interaction.id}:background`, quiz.imageAssetId),
        ...(quiz.images ?? []).map((image, index) => ({ id: image.id, label: letter(index), assetId: image.assetId || undefined })),
      ];
    }
    case InteractionType.PhotoRangeQuiz: {
      const quiz = interaction as IPhotoRangeQuiz;
      return [background(`${interaction.id}:base`, quiz.baseImage), ...fromAnswers(quiz.answers)];
    }
    case InteractionType.PowerSlide:
      // Every filled asset slot, grouped per slide; slots are numbered within their slide.
      return ((interaction as IPowerSlide).slides ?? []).flatMap((slide, slideIndex) =>
        (slide.assets ?? []).map((asset, index) => ({
          id: `${slide.id}:${asset.slotId}`,
          label: String(index + 1),
          assetId: asset.assetId || undefined,
          group: slideIndex + 1,
        }))
      );
    case InteractionType.PdfPopup:
    case InteractionType.AmbientAudio:
      // One file; the list shows an icon for it (it has no picture) — see AssetLanguageList.
      return single(interaction, (interaction as Interaction & { assetId?: string }).assetId);
    case InteractionType.AudioPopup: {
      const popup = interaction as IAudioPopup;
      return [
        { id: `${interaction.id}:image`, label: "", assetId: popup.imageAssetId || undefined, nameKey: "asset_localization.image" },
        { id: `${interaction.id}:audio`, label: "", assetId: popup.audioAssetId || undefined, nameKey: "asset_localization.audio" },
      ];
    }
    case InteractionType.LabelDragAndDropQuiz:
      // The image the labels are dropped onto.
      return single(interaction, (interaction as ILabelDragAndDropQuiz).imageAssetId);
    default:
      return [];
  }
};
